import React from 'react';

interface CountryFlagProps {
  /**
   * ISO 3166-1 alpha-2 country code (e.g. "PL", "DE", "GB").
   * Case insensitive. Returns null if empty.
   */
  countryCode: string | null | undefined;
  /** Height in pixels. Width auto-scales (~4:3 aspect ratio). Default 18. */
  size?: number;
  /** Optional override of alt text (defaults to countryCode). */
  alt?: string;
  /** Inline style overrides. */
  style?: React.CSSProperties;
}

/**
 * Render a country flag as an SVG image. Uses flagcdn.com (free, fast CDN).
 *
 * SVG (not PNG) so it scales crisp to any pixel size — flagcdn.com only serves PNGs
 * at fixed dimensions (16x12, 20x15, 24x18, 28x21, 32x24, ...) and returns 404 for
 * arbitrary sizes like 27x20. SVG sidesteps that completely.
 *
 * Works on every OS (Linux, Windows, macOS, mobile) — unlike Unicode regional indicator
 * emojis which Windows renders as plain letters (e.g. "DE" instead of the German flag).
 */
const CountryFlag: React.FC<CountryFlagProps> = ({ countryCode, size = 18, alt, style }) => {
  if (!countryCode) return null;

  // Some country codes we use don't match ISO; remap known cases:
  const remap: Record<string, string> = {
    UK: 'gb', // Krosno uses "UK" in some configs; ISO is GB
  };

  const normalized = countryCode.toUpperCase();
  const isoCode = (remap[normalized] || normalized).toLowerCase();

  // Approximate width — flag aspect ratio is ~4:3, but SVG handles real proportions.
  const width = Math.round(size * 4 / 3);

  return (
    <img
      src={`https://flagcdn.com/${isoCode}.svg`}
      width={width}
      height={size}
      alt={alt ?? countryCode}
      loading="lazy"
      style={{
        display: 'inline-block',
        verticalAlign: 'middle',
        borderRadius: '2px',
        boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.06)',
        objectFit: 'cover',
        ...style,
      }}
      onError={(e) => {
        // Hide broken image (unknown country code)
        (e.target as HTMLImageElement).style.display = 'none';
      }}
    />
  );
};

export default CountryFlag;
